import { Component } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { CalloutText } from '@components/CalloutText';
import {
  CopyIcon,
  FacebookIcon,
  LinkedinIcon,
  ShareIcon,
  WhatsAppIcon
} from '@components/Icon';
import { Link } from '@components/Link';
import { Modal } from '@components/Modal';
import { Text } from '@components/Text';
import Div from '../../../../../../../common/components/Div/Div';
import IconShowCase from '../../../../../../../common/components/IconShowCase/IconShowCase';
import { SOCIAL_MEDIA_PLATFORM, handleSocialLinkClick } from './socialMedia';

@withTranslation()
export default class ShareAdActionLink extends Component {
  static propTypes = {
    t: PropTypes.func.isRequired,
    job: PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      liveJobLink: PropTypes.string.isRequired
    }),
    brandName: PropTypes.string.isRequired,
    handleSocialShareAdIconClicked: PropTypes.func.isRequired,
    handleSocialIconClicked: PropTypes.func.isRequired
  };

  constructor(props) {
    super(props);

    this.state = {
      isOpenSocialShareModal: false,
      showCalloutOnSocialShareModal: false,
      isSocialShareCopyError: false
    };
  }

  async openSocialShareModal() {
    const {
      job: { id },
      handleSocialShareAdIconClicked
    } = this.props;
    this.setState({
      isOpenSocialShareModal: true
    });

    await handleSocialShareAdIconClicked({ id });
  }

  closeSocialShareModal() {
    this.setState({
      isOpenSocialShareModal: false,
      showCalloutOnSocialShareModal: false,
      isSocialShareCopyError: false
    });
  }

  fallbackCopyTextToClipboard(textUrl) {
    let result;
    const input = document.createElement('textarea');
    input.innerHTML = textUrl;
    document.body.appendChild(input);
    input.select();

    try {
      result = document.execCommand('copy');
      if (result) {
        this.setState({
          showCalloutOnSocialShareModal: true,
          isSocialShareCopyError: false
        });
      } else {
        this.setState({
          showCalloutOnSocialShareModal: true,
          isSocialShareCopyError: true
        });
      }
    } catch (err) {
      this.setState({
        showCalloutOnSocialShareModal: true,
        isSocialShareCopyError: true
      });
      console.error('Fallback: Oops, unable to copy', err);
    }

    document.body.removeChild(input);
    return result;
  }

  async copyJobUrlToClipBoard(textUrl) {
    if (!navigator.clipboard) {
      this.fallbackCopyTextToClipboard(textUrl);
      return;
    }

    try {
      const toCopy = textUrl || location.href;
      await navigator.clipboard.writeText(toCopy);
      this.setState({
        showCalloutOnSocialShareModal: true,
        isSocialShareCopyError: false
      });
    } catch (err) {
      this.setState({
        showCalloutOnSocialShareModal: true,
        isSocialShareCopyError: true
      });
      console.error('Failed to copy: ', err);
    }

    return;
  }

  render() {
    const {
      isOpenSocialShareModal,
      showCalloutOnSocialShareModal,
      isSocialShareCopyError
    } = this.state;
    const {
      job: { id, title },
      t
    } = this.props;
    const themeType = isSocialShareCopyError ? 'error' : 'success';
    const message = isSocialShareCopyError
      ? `${t('dashboardPage.shareAd.copyLinkError')}`
      : `${t('dashboardPage.shareAd.copiedSuccessfully')}`;

    const titleComponent = (text) => <Text>{text}</Text>;

    const socialIconsList = [
      {
        icon: <FacebookIcon size="xxlarge" />,
        title: titleComponent(t('dashboardPage.shareAd.facebook')),
        onClick: handleSocialLinkClick.bind(this),
        key: SOCIAL_MEDIA_PLATFORM.FACEBOOK
      },
      {
        icon: <LinkedinIcon size="xxlarge" />,
        title: titleComponent(t('dashboardPage.shareAd.linkedin')),
        onClick: handleSocialLinkClick.bind(this),
        key: SOCIAL_MEDIA_PLATFORM.LINKEDIN
      },
      {
        icon: <CopyIcon size="xxlarge" variant="outline" />,
        title: titleComponent(t('dashboardPage.shareAd.copyLink')),
        onClick: handleSocialLinkClick.bind(this),
        key: 'copy'
      },
      {
        icon: <WhatsAppIcon size="xxlarge" />,
        title: titleComponent(t('applications.chatModal.action.whatsApp')),
        onClick: handleSocialLinkClick.bind(this),
        key: SOCIAL_MEDIA_PLATFORM.WHATSAPP
      }
    ];

    return (
      <Div marginBottom="medium">
        <Modal
          title={t('dashboardPage.shareAd.title', { jobTitle: title })}
          visible={isOpenSocialShareModal}
          onRequestClose={this.closeSocialShareModal.bind(this)}
          testID={`social-share-modal-${id}`}
        >
          {showCalloutOnSocialShareModal ? (
            <CalloutText tone={themeType} testID={`copy-callout-text-${id}`}>
              {message}
            </CalloutText>
          ) : null}
          <IconShowCase
            marginTop="medium"
            dataSet={socialIconsList}
            data-test-key={`social-share-icons-${id}`}
            maxRowItems={3}
          />
        </Modal>
        <Link
          variant="secondary"
          size="medium"
          weight="regular"
          align={{
            mobile: 'vertical',
            tablet: 'horizontal',
            desktop: 'horizontal'
          }}
          icon={<ShareIcon variant="outline" size="medium" />}
          onPress={() => this.openSocialShareModal()}
          testID={`social-share-${id}-link-key`}
        >
          {t('dashboardPage.action.shareAd')}
        </Link>
      </Div>
    );
  }
}
